/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from "react";

import {
	Box,
	Button,
	Divider,
	HStack,
	Icon,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
	FormControl,
	FormLabel,
	Input,
	FormErrorMessage,
	Textarea,
	Select,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverCloseButton,
	useDisclosure,
	Tag,
	TagLabel,
	TagCloseButton,
	StackProps,
	useToast,
} from "@chakra-ui/react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import { Calendar } from "react-date-range";
import { es } from "react-date-range/dist/locale";
import { FiEye } from "react-icons/fi";
import * as Yup from "yup";

import { getComentarios, postComentario, putComentario } from "api/comentarios";
import { getComunicaciones, postComunicacion, putComunicacion } from "api/comunicaciones";
import { TEXTOS_EVENTO } from "common/components/Comentarios";
import FullsizeSpinner from "common/components/FullsizeSpinner";
import { ITECommentInput, ITECommentOutput } from "components/ITE/types/comentarios";
import { ComunicacionType, ITEComunicacionInput, ITEComunicacionOutput } from "components/ITE/types/comunicaciones";
import { ActivoCompletoOutput } from "components/Portfolio/types/activos";
import { actions as portfolioActions } from "store/portfolio/reducer";
import { useAppDispatch, useAppSelector } from "store/store";

interface IProps {
	onOpenComunicaciones: () => void;
}

export const BotonComunicaciones: React.FC<IProps> = (props) => {
	const { onOpenComunicaciones } = props;

	return (
		<IconButton
			title="Histórico"
			aria-label="historico"
			colorScheme="gray"
			height="2rem"
			minW="1rem"
			style={{ aspectRatio: "1" }}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onOpenComunicaciones();
			}}
		>
			<Icon as={FiEye} fontSize="1rem" color="gray.600" strokeWidth={"1"} />
		</IconButton>
	);
};

interface ModalComunicacionesProps {
	asset?: ActivoCompletoOutput;
	onClose: () => void;
}

export const ModalComunicaciones: React.FC<ModalComunicacionesProps> = (props) => {
	const { asset } = props;

	const features = useAppSelector((state) => state.user.features.data);
	const email = useAppSelector((state) => state.user.userEmail);
	const [loadingComentarios, setLoadingComentarios] = useState(true);
	const [loadingComunicaciones, setLoadingComunicaciones] = useState(true);
	const [loading, setLoading] = useState(true);
	const [comentarios, setComentarios] = useState<ITECommentOutput[]>([]);
	const [comunicaciones, setComunicaciones] = useState<ITEComunicacionOutput[]>([]);
	const [items, setItems] = useState<
		Array<
			| { type: "comentario"; n: number; data: ITECommentOutput }
			| { type: "comunicacion"; n: number; data: ITEComunicacionOutput }
		>
	>([]);
	const [nuevoComentario, setNuevoComentario] = useState<ITECommentInput>();
	const [nuevaComunicacion, setNuevaComunicacion] = useState<ITEComunicacionInput>();

	const hasAddCommentFeature = features.indexOf("add-comment") >= 0;

	useEffect(() => {
		if (!asset?.ite_id) return;
		getComentarios({ iteId: asset.ite_id })
			.then((res) => setComentarios(res.data))
			.then(() => setLoadingComentarios(false));
		getComunicaciones({ iteId: asset.ite_id })
			.then((res) => setComunicaciones(res.data))
			.then(() => setLoadingComunicaciones(false));
	}, [asset?.ite_id]);

	useEffect(() => {
		if (loadingComentarios || loadingComunicaciones) return;
		const normalizedComentariosExtra = comentarios
			.filter((it) => it.event === "extra")
			.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
			.map((c, i) => ({ type: "comentario" as const, n: i + 1, data: c }));
		const normalizedComentarios = comentarios
			.filter((it) => it.event !== "extra")
			.map((c) => ({ type: "comentario" as const, n: 0, data: c }));
		const normalizedComunicaciones = comunicaciones
			.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
			.map((c, i) => ({ type: "comunicacion" as const, n: i + 1, data: c }));
		setItems(
			[...normalizedComentariosExtra, ...normalizedComentarios, ...normalizedComunicaciones].sort(
				(a, b) => new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime(),
			),
		);
		setLoading(false);
	}, [comentarios, comunicaciones, loadingComentarios, loadingComunicaciones]);

	const onAddComentario = () => {
		if (!asset) return;
		setNuevoComentario({
			iteId: asset.ite_id,
			comment: "",
			event: "extra",
		});
	};

	const onAddComunicacion = () => {
		if (!asset) return;
		setNuevaComunicacion({
			ite_id: asset.ite_id,
			comment: "",
			type: "mail_manual",
		});
	};

	const onClose = () => {
		setLoadingComentarios(true);
		setLoadingComunicaciones(true);
		setLoading(true);
		props.onClose();
	};

	return (
		<Modal isOpen={!!asset} onClose={onClose} size="6xl" closeOnEsc={false} closeOnOverlayClick={false}>
			<ModalOverlay />
			<ModalContent maxH="85vh" maxW="85vw">
				<ModalHeader>
					<HStack justifyContent="space-between" mr="2rem">
						<VStack align="start">
							<Text textStyle="body2" color="#006935">
								Histórico de eventos y comunicaciones
							</Text>
							<Text>{asset?.nombre}</Text>
						</VStack>
						<HStack gap="1.5rem">
							<Button
								size="lg"
								bg="gray.400"
								fontWeight="normal"
								color="white"
								fontSize="small"
								w="12rem"
								onClick={onAddComunicacion}
								disabled={!!nuevaComunicacion || !!nuevoComentario || !hasAddCommentFeature}
							>
								Añadir comunicación extra
							</Button>
							<Button
								size="lg"
								bg="gray.400"
								fontWeight="normal"
								color="white"
								fontSize="small"
								w="12rem"
								onClick={onAddComentario}
								disabled={!!nuevoComentario || !!nuevaComunicacion || !hasAddCommentFeature}
							>
								Añadir comentario
							</Button>
						</HStack>
					</HStack>
				</ModalHeader>
				<ModalCloseButton color="gray.500" />
				<Divider />
				<ModalBody overflowY="auto" overflowX="hidden" py="1rem">
					{loading ? (
						<FullsizeSpinner />
					) : (
						<>
							{nuevoComentario && !nuevaComunicacion && (
								<>
									<Comentario
										data={{
											...nuevoComentario,
											author: email,
											created_at: new Date().toISOString(),
											id: "",
											ite_id: asset?.ite_id || "",
										}}
										n={comentarios.filter((it) => it.event === "extra").length + 1}
										editable={hasAddCommentFeature}
										setComentarios={setComentarios}
										setNuevoComentario={setNuevoComentario}
										onDiscard={() => setNuevoComentario(undefined)}
									/>
									{items.length > 0 && <Divider my="1rem" />}
								</>
							)}
							{nuevaComunicacion && (
								<>
									<Comunicacion
										data={{
											...nuevaComunicacion,
											author: email,
											created_at: new Date().toISOString(),
											id: "",
											ite_id: asset?.ite_id || "",
										}}
										n={comunicaciones.length + 1}
										assetId={asset?.id || ""}
										editable={hasAddCommentFeature}
										setComunicaciones={setComunicaciones}
										setNuevaComunicacion={setNuevaComunicacion}
										onDiscard={() => setNuevaComunicacion(undefined)}
										updatePortfolioData
									/>
									{items.length > 0 && <Divider my="1rem" />}
								</>
							)}
							{items.map((it, i) => {
								return (
									<React.Fragment key={it.data.id}>
										{i !== 0 && <Divider my="1rem" />}
										{it.type === "comentario" ? (
											<Comentario
												data={{
													...it.data,
													comment:
														it.data.event !== "extra"
															? `${TEXTOS_EVENTO[it.data.event]}${it.data.comment ? ` - ${it.data.comment}` : ""}`
															: it.data.comment,
												}}
												n={it.n}
												setComentarios={setComentarios}
												setNuevoComentario={setNuevoComentario}
												editable={it.data.event === "extra"}
											/>
										) : (
											<Comunicacion
												data={it.data}
												n={it.n}
												assetId={asset?.id || ""}
												setComunicaciones={setComunicaciones}
												setNuevaComunicacion={setNuevaComunicacion}
												updatePortfolioData={items.filter((e) => e.type === "comunicacion")[0].data.id === it.data.id}
											/>
										)}
									</React.Fragment>
								);
							})}
						</>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

interface ComentarioProps {
	data: ITECommentOutput;
	n: number;
	setComentarios: React.Dispatch<React.SetStateAction<ITECommentOutput[]>>;
	setNuevoComentario: React.Dispatch<React.SetStateAction<ITECommentInput | undefined>>;
	editable?: boolean;
	onDiscard?: () => void;
}
interface ComentarioFormValues {
	comment: string;
}
const comentarioSchema = Yup.object({
	comment: Yup.string(),
});
const Comentario: React.FC<ComentarioProps> = (props) => {
	const { data, n, editable = true, setComentarios, setNuevoComentario, onDiscard } = props;
	const toast = useToast();
	const formatter = Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" });
	const initialValues: ComentarioFormValues = {
		comment: data.comment || "",
	};
	const onSubmit = async (values: ComentarioFormValues, actions: FormikHelpers<ComentarioFormValues>) => {
		if (!editable) return;
		if (data.id) {
			try {
				const updatedComentario = await putComentario({ id: data.id, data: values });
				setComentarios((prevState) => prevState.map((e) => (e.id === data.id ? updatedComentario.data : e)));
				actions.resetForm({ values: { comment: updatedComentario.data.comment } });
			} catch (_) {
				toast({
					title: "Error al guardar comentario",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		} else {
			try {
				const newComentario = await postComentario({ ...values, event: "extra", iteId: data.ite_id });
				setNuevoComentario(undefined);
				setComentarios((prevState) => [newComentario.data, ...prevState]);
			} catch (_) {
				toast({
					title: "Error al crear comentario",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		}
	};
	return (
		<Formik initialValues={initialValues} validationSchema={comentarioSchema} onSubmit={onSubmit}>
			{({ handleSubmit, errors, touched, isSubmitting, dirty }) => (
				<Form onSubmit={handleSubmit} style={{ width: "100%" }}>
					<HStack w="100%" justifyContent="space-between" mb="1rem">
						<Text color="gray.800" fontWeight="700" fontSize="small">
							{TEXTOS_EVENTO[data.event]}
							{data.event === "extra" ? ` ${n}` : ""}
						</Text>
						{editable && (
							<HStack>
								{!data.id && (
									<Button
										py="0.25rem"
										variant="outline"
										color="gray.600"
										borderColor="gray.600"
										fontSize="small"
										isDisabled={isSubmitting}
										onClick={onDiscard}
									>
										Descartar
									</Button>
								)}
								<Button
									type="submit"
									py="0.25rem"
									variant="outline"
									color="gray.600"
									borderColor="gray.600"
									fontSize="small"
									isDisabled={isSubmitting || !dirty}
									isLoading={isSubmitting}
								>
									Guardar
								</Button>
							</HStack>
						)}
					</HStack>
					<VStack w="50%" alignItems="flex-start">
						<HStack w="100%" gap="1rem">
							<Box flex="1">
								<FormLabel>Autor</FormLabel>
								<Input isReadOnly value={data.author} />
							</Box>
							<Box flex="1">
								<FormLabel>Fecha</FormLabel>
								<Input isReadOnly value={formatter.format(new Date(data.created_at))} />
							</Box>
						</HStack>
						<Field name="comment">
							{({ field }) => (
								<FormControl isReadOnly={!editable} isInvalid={!!errors.comment && touched.comment}>
									<FormLabel htmlFor={field.name}>Comentario</FormLabel>
									<Textarea {...field} id={field.name} placeholder="Añadir comentario" />
									<FormErrorMessage>{errors.comment}</FormErrorMessage>
								</FormControl>
							)}
						</Field>
					</VStack>
				</Form>
			)}
		</Formik>
	);
};

interface ComunicacionProps {
	data: ITEComunicacionOutput;
	n: number;
	assetId: string;
	setComunicaciones: React.Dispatch<React.SetStateAction<ITEComunicacionOutput[]>>;
	setNuevaComunicacion: React.Dispatch<React.SetStateAction<ITEComunicacionInput | undefined>>;
	editable?: boolean;
	onDiscard?: () => void;
	updatePortfolioData?: boolean;
}
interface ComunicacionFormValues {
	comment: string;
	type: ComunicacionType;
	revised_at?: string;
	issuer?: string;
	date?: string;
	recipients?: string[];
	title?: string;
	comments?: string;
	response_issuer?: string;
	response_date?: string;
	response_recipients?: string[];
	response_title?: string;
	response_comments?: string;
}
const comunicacionSchema = Yup.object({
	comment: Yup.string(),
	type: Yup.string().oneOf(["mail_manual", "mail_plataforma", "telefono"]),
	revised_at: Yup.string(),
	issuer: Yup.string(),
	date: Yup.string(),
	recipients: Yup.array().of(Yup.string()),
	title: Yup.string(),
	comments: Yup.string(),
	response_issuer: Yup.string(),
	response_date: Yup.string(),
	response_recipients: Yup.array().of(Yup.string()),
	response_title: Yup.string(),
	response_comments: Yup.string(),
});

const Comunicacion: React.FC<ComunicacionProps> = (props) => {
	const {
		data,
		n,
		editable = true,
		setComunicaciones,
		setNuevaComunicacion,
		onDiscard,
		updatePortfolioData,
		assetId,
	} = props;
	const dispatch = useAppDispatch();
	const toast = useToast();
	const formatter = Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" });
	const initialValues: ComunicacionFormValues = {
		comment: data.comment || "",
		type: data.type || "mail_manual",
		revised_at: data.revised_at || "",
		issuer: data.issuer || "",
		date: data.date || "",
		recipients: data.recipients || [],
		title: data.title || "",
		comments: data.comments || "",
		response_issuer: data.response_issuer || "",
		response_date: data.response_date || "",
		response_recipients: data.response_recipients || [],
		response_title: data.response_title || "",
		response_comments: data.response_comments || "",
	};
	const onSubmit = async (values: ComunicacionFormValues, actions: FormikHelpers<ComunicacionFormValues>) => {
		if (!editable) return;
		if (data.id) {
			try {
				const updatedComunicacion = await putComunicacion({ id: data.id, data: values });
				setComunicaciones((prevState) => prevState.map((e) => (e.id === data.id ? updatedComunicacion.data : e)));
				actions.resetForm({
					values: {
						comment: updatedComunicacion.data.comment || "",
						type: updatedComunicacion.data.type || "mail_manual",
						revised_at: updatedComunicacion.data.revised_at || "",
						issuer: updatedComunicacion.data.issuer || "",
						date: updatedComunicacion.data.date || "",
						recipients: updatedComunicacion.data.recipients || [],
						title: updatedComunicacion.data.title || "",
						comments: updatedComunicacion.data.comments || "",
						response_issuer: updatedComunicacion.data.response_issuer || "",
						response_date: updatedComunicacion.data.response_date || "",
						response_recipients: updatedComunicacion.data.response_recipients || [],
						response_title: updatedComunicacion.data.response_title || "",
						response_comments: updatedComunicacion.data.response_comments || "",
					},
				});
				if (updatePortfolioData) {
					dispatch(
						portfolioActions.editarComunicacionActivo({ assetId: assetId, comunicacion: updatedComunicacion.data }),
					);
				}
			} catch (_) {
				toast({
					title: "Error al guardar comunicación",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		} else {
			try {
				const newComunicacion = await postComunicacion({ ...values, ite_id: data.ite_id });
				setNuevaComunicacion(undefined);
				setComunicaciones((prevState) => [newComunicacion.data, ...prevState]);
				if (updatePortfolioData) {
					dispatch(portfolioActions.editarComunicacionActivo({ assetId: assetId, comunicacion: newComunicacion.data }));
				}
			} catch (_) {
				toast({
					title: "Error al crear comnicación",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		}
	};
	return (
		<Formik initialValues={initialValues} validationSchema={comunicacionSchema} onSubmit={onSubmit}>
			{({ handleSubmit, errors, touched, isSubmitting, dirty, setFieldValue, values }) => {
				return (
					<Form onSubmit={handleSubmit} style={{ width: "100%" }}>
						<HStack w="100%" justifyContent="space-between" mb="1rem">
							<Text color="gray.800" fontWeight="700" fontSize="small">{`Comunicación ${n}`}</Text>
							{editable && (
								<HStack>
									{!data.id && (
										<Button
											py="0.25rem"
											variant="outline"
											color="gray.600"
											borderColor="gray.600"
											fontSize="small"
											isDisabled={isSubmitting}
											onClick={onDiscard}
										>
											Descartar
										</Button>
									)}
									<Button
										type="submit"
										py="0.25rem"
										variant="outline"
										color="gray.600"
										borderColor="gray.600"
										fontSize="small"
										isDisabled={isSubmitting || !dirty}
										isLoading={isSubmitting}
									>
										Guardar
									</Button>
								</HStack>
							)}
						</HStack>
						<HStack w="100%" gap="1rem" align="stretch">
							<VStack
								flex="4"
								alignItems="flex-start"
								p="1rem"
								borderRadius="0.5rem"
								borderColor="gray.200"
								borderWidth="1px"
								minW="0"
								display="flex"
							>
								<Text color="#006935" fontSize="small">
									{values.type === "telefono" ? "Llamada" : "Mail"}
								</Text>
								<HStack w="100%" gap="1rem">
									<Box flex="1">
										<Field name="issuer">
											{({ field }) => (
												<FormControl
													isReadOnly={!editable || data.type === "mail_plataforma"}
													isInvalid={!!errors.issuer && touched.issuer}
												>
													<FormLabel htmlFor={field.name}>Emisor</FormLabel>
													<Input {...field} id={field.name} placeholder="Emisor" />
													<FormErrorMessage>{errors.issuer}</FormErrorMessage>
												</FormControl>
											)}
										</Field>
									</Box>
									<Box flex="1">
										<Field name="date">
											{({ field }) => (
												<FormControl
													isReadOnly={!editable || data.type === "mail_plataforma"}
													isInvalid={!!errors.date && touched.date}
												>
													<FormLabel htmlFor={field.name}>Fecha</FormLabel>
													<CalendarInput
														value={field.value ? new Date(field.value) : undefined}
														onChange={(date) => setFieldValue(field.name, date.toISOString())}
														isReadOnly={!editable || data.type === "mail_plataforma"}
														placeholder="Fecha"
													/>
													<FormErrorMessage>{errors.date}</FormErrorMessage>
												</FormControl>
											)}
										</Field>
									</Box>
								</HStack>
								<Field name="recipients">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.recipients && touched.recipients}
										>
											<FormLabel htmlFor={field.name}>Destinatarios</FormLabel>
											<TagInput
												value={field.value}
												onChange={(v) => setFieldValue(field.name, v)}
												isReadOnly={!editable || data.type === "mail_plataforma"}
												placeholder="Destinatarios"
											/>
											<FormErrorMessage>{errors.recipients}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="title">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.title && touched.title}
										>
											<FormLabel htmlFor={field.name}>Título</FormLabel>
											<Input {...field} id={field.name} placeholder="Título" />
											<FormErrorMessage>{errors.title}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="comments">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.comments && touched.comments}
											flex="1"
											display="flex"
											flexDirection="column"
										>
											<FormLabel htmlFor={field.name}>
												{values.type === "telefono" ? "Comentarios de la llamada" : "Cuerpo del email"}
											</FormLabel>
											<Textarea {...field} id={field.name} placeholder="Añadir comentario" flex="1" resize="none" />
											<FormErrorMessage>{errors.comments}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
							</VStack>

							<VStack
								flex="4"
								alignItems="flex-start"
								p="1rem"
								borderRadius="0.5rem"
								borderColor="gray.200"
								borderWidth="1px"
								minW="0"
								display="flex"
							>
								<Text color="#006935" fontSize="small">
									{"Respuesta"}
								</Text>
								<HStack w="100%" gap="1rem">
									<Box flex="1">
										<Field name="response_issuer">
											{({ field }) => (
												<FormControl
													isReadOnly={!editable}
													isInvalid={!!errors.response_issuer && touched.response_issuer}
												>
													<FormLabel htmlFor={field.name}>Emisor</FormLabel>
													<Input {...field} id={field.name} placeholder="Emisor" />
													<FormErrorMessage>{errors.response_issuer}</FormErrorMessage>
												</FormControl>
											)}
										</Field>
									</Box>
									<Box flex="1">
										<Field name="response_date">
											{({ field }) => (
												<FormControl isReadOnly={!editable} isInvalid={!!errors.response_date && touched.response_date}>
													<FormLabel htmlFor={field.name}>Fecha</FormLabel>
													<CalendarInput
														value={field.value ? new Date(field.value) : undefined}
														onChange={(date) => setFieldValue(field.name, date.toISOString())}
														isReadOnly={!editable}
														placeholder="Fecha"
													/>
													<FormErrorMessage>{errors.response_date}</FormErrorMessage>
												</FormControl>
											)}
										</Field>
									</Box>
								</HStack>
								<Field name="response_recipients">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable}
											isInvalid={!!errors.response_title && touched.response_title}
											mb="0"
										>
											<FormLabel htmlFor={field.name}>Destinatarios</FormLabel>
											<TagInput
												value={field.value}
												onChange={(v) => setFieldValue(field.name, v)}
												isReadOnly={!editable}
												placeholder="Destinatarios"
											/>
											<FormErrorMessage>{errors.response_title}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="response_title">
									{({ field }) => (
										<FormControl isReadOnly={!editable} isInvalid={!!errors.response_title && touched.response_title}>
											<FormLabel htmlFor={field.name}>Título</FormLabel>
											<Input {...field} id={field.name} placeholder="Título" />
											<FormErrorMessage>{errors.response_title}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="response_comments">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable}
											isInvalid={!!errors.response_comments && touched.response_comments}
											flex="1"
											display="flex"
											flexDirection="column"
										>
											<FormLabel htmlFor={field.name}>
												{values.type === "telefono" ? "Comentarios de la llamada" : "Cuerpo del email"}
											</FormLabel>
											<Textarea {...field} id={field.name} placeholder="Añadir comentario" flex="1" resize="none" />
											<FormErrorMessage>{errors.response_comments}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
							</VStack>

							<VStack flex="3" alignItems="flex-start" minW="0" display="flex">
								<Box w="100%">
									<FormLabel>Autor</FormLabel>
									<Input isReadOnly value={data.author} />
								</Box>
								<Box w="100%">
									<FormLabel>Fecha</FormLabel>
									<Input isReadOnly value={formatter.format(new Date(data.created_at))} />
								</Box>
								<Field name="type">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.type && touched.type}
										>
											<FormLabel htmlFor={field.name}>Tipo de comunicación</FormLabel>
											<Select
												{...field}
												id={field.name}
												pointerEvents={!editable || data.type === "mail_plataforma" ? "none" : undefined}
											>
												<option value="mail_manual">Mail manual</option>
												<option value="telefono">Teléfono</option>
												<option
													value="mail_plataforma"
													disabled
													style={{ color: data.type === "mail_plataforma" ? "gray" : undefined }}
												>
													Mail plataforma
												</option>
											</Select>
											<FormErrorMessage>{errors.type}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="revised_at">
									{({ field }) => (
										<FormControl isReadOnly={!editable} isInvalid={!!errors.revised_at && touched.revised_at}>
											<FormLabel htmlFor={field.name}>Fecha de revisión</FormLabel>
											<CalendarInput
												value={field.value ? new Date(field.value) : undefined}
												onChange={(date) => setFieldValue(field.name, date.toISOString())}
												isReadOnly={!editable}
												placeholder="Fecha de revisión"
											/>
											<FormErrorMessage>{errors.revised_at}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="comment">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable}
											isInvalid={!!errors.comment && touched.comment}
											flex="1"
											display="flex"
											flexDirection="column"
										>
											<FormLabel htmlFor={field.name}>Comentario</FormLabel>
											<Textarea {...field} id={field.name} placeholder="Añadir comentario" flex="1" resize="none" />
											<FormErrorMessage>{errors.comment}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
							</VStack>
						</HStack>
					</Form>
				);
			}}
		</Formik>
	);
};

interface CalendarProps {
	value: Date | undefined;
	onChange?: (date: Date) => void;
	isReadOnly?: boolean;
	placeholder?: string;
}
const CalendarInput: React.FC<CalendarProps> = (props) => {
	const { value, onChange, isReadOnly, placeholder } = props;
	const { onToggle, isOpen, onClose } = useDisclosure();
	const formatter = Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" });
	return (
		<Popover
			returnFocusOnClose={false}
			isOpen={isOpen && !isReadOnly}
			onClose={onClose}
			placement="auto"
			closeOnBlur={true}
			isLazy
		>
			<PopoverTrigger>
				<Input
					onClick={onToggle}
					value={value ? formatter.format(value) : ""}
					cursor={isReadOnly ? undefined : "pointer"}
					isReadOnly={isReadOnly}
					placeholder={placeholder}
				/>
			</PopoverTrigger>
			<PopoverContent w="100%" h="100%">
				<PopoverCloseButton />
				<Calendar
					date={value}
					onChange={(date) => {
						onChange?.(date);
						onToggle();
					}}
					locale={es}
				/>
			</PopoverContent>
		</Popover>
	);
};

interface TagInputProps {
	value: string[];
	onChange?: (v: string[]) => void;
	isReadOnly?: boolean;
	placeholder?: string;
}
const TagInput: React.FC<TagInputProps> = (props) => {
	const { value, onChange, isReadOnly, placeholder } = props;
	const [inputValue, setInputValue] = useState<string>("");
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const styles: StackProps = {
		maxWidth: "100%",
		border: "1px solid",
		borderColor: "gray.200",
		borderRadius: "0.375rem",
		flexWrap: "wrap",
		...(value.length && { padding: "0.5rem" }),
	};
	const renderTags = useMemo(
		() =>
			value.map((tag, index) => (
				<Tag key={index} marginInlineStart="0 !important" marginInlineEnd="0.5rem !important" mt="0.5rem !important">
					<TagLabel color="gray.500">{tag}</TagLabel>
					{!isReadOnly && <TagCloseButton onClick={() => removeTag(index)} />}
				</Tag>
			)),
		[value, isReadOnly],
	);
	const removeTag = (index: number) => {
		const newTags = [...value];
		newTags.splice(index, 1);
		onChange?.(newTags);
		setInputValue("");
	};
	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};
	const handleOnKeyDown = (e) => {
		if (e.key === "Enter" && isFocused) {
			e.preventDefault();
		}
		if (["Enter", "Tab", ","].includes(e.key) && inputValue) {
			e.preventDefault();
			if (value.includes(inputValue)) return;
			onChange?.([...value, inputValue]);
			setInputValue("");
		}
	};
	const handleOnFocus = () => {
		setIsFocused(true);
	};

	const handleOnBlur = () => {
		setIsFocused(false);
		if (inputValue) {
			if (value.includes(inputValue)) return;
			onChange?.([...value, inputValue]);
			setInputValue("");
		}
	};
	return (
		<HStack {...styles}>
			<Input
				placeholder={placeholder}
				margin="0"
				flex={1}
				px="1rem"
				minWidth="100%"
				border="none"
				onChange={handleOnChange}
				onKeyDown={handleOnKeyDown}
				onFocus={handleOnFocus}
				onBlur={handleOnBlur}
				value={inputValue}
				isReadOnly={isReadOnly}
			/>
			{renderTags}
		</HStack>
	);
};
